// @ts-nocheck -- legacy typings pending cleanup; runtime behavior unchanged
/**
 * Engine de interações contextuais — sem alarmismo genérico.
 */

import {
  canonicalDisplayLabel,
  canonicalSubstanceKey,
  isExplicitRedoseLike,
  isLongHalfLifeMaintenanceKey,
  isMaintenanceLikeLog,
  isPotentiallyAcuteOrPrnKey,
  isStimulantKey,
  shouldFlagSelfOverlap,
  uniqueCanonicalKeys,
} from "@/lib/clinical/substanceRules";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { buildSessionContext } from "@/domain/sessionContextEngine";
import {
  getInteractionMatrixRules,

  temporalizeMatrixRule,
  type ClinicalInteractionMatrixRule,
} from "@/domain/clinicalInteractionMatrix";

export type InteractionRelevance =
  | "informativa"
  | "monitorar"
  | "cautela"
  | "relevante"
  | "urgente";

export type InteractionCategory =
  | "metabolica"
  | "farmacodinamica"
  | "comportamental"
  | "substancia-uso"
  | "contextual";

export interface InteractionInput {
  activeMedications: {
    name: string;
    classHint?: string;
    frequency?: string | null;
    currentDose?: number | null;
  }[];
  recentDoses: {
    substanceName: string;
    actualTime: string | Date;
    logType?: string;
    doseAmount?: number | null;
    caffeineMg?: number | null;
    sleepDeprivation?: number | null;
  }[];
  recentSubstanceUse: { substanceName: string; usedAt: string | Date }[];
  sessionContext?: {
    sleepHours?: number | null;
    caffeine?: string | null;
    suicideRisk?: string | null;
  };
  /** Full original records retain formulation, units, ethanol conversion and route. */
  exposureDoses?: any[];
  bodyWeightKg?: number | null;
}

export interface InteractionFinding {
  category: InteractionCategory;
  relevance: InteractionRelevance;
  summary: string;
  detail?: string;
  involvedSubstances: string[];
  mechanism?: string;
  monitor?: string[];
  action?: string;
  confidence: "baixa" | "moderada" | "alta";
  whyNow?: string;
  temporalBasis?: string;
  evidence?: string;
  phenotype?: string[];
  redFlags?: string[];
}

const norm = (s: string) => s.toLowerCase();
const includesAny = (s: string, list: string[]) =>
  list.some((x) => norm(s).includes(x));

const STIMULANTS = [
  "metilfenidato",
  "ritalina",
  "concerta",
  "foq",
  "lisdexanfetamina",
  "lisdexamfetamine",
  "venvanse",
  "lyberdia",
  "juneve",
  "vyvanse",
  "elvanse",
  "anfetamina",
  "dextroanfetamina",
];
const BENZOS = [
  "clonazepam",
  "rivotril",
  "klonopin",
  "diazepam",
  "alprazolam",
  "lorazepam",
  "midazolam",
];
const OPIOIDS = [
  "opioide",
  "morfina",
  "oxicodona",
  "codeina",
  "codeína",
  "fentanil",
  "metadona",
  "buprenorfina",
  "tramadol",
];
const SEDATIVE = ["quetiapina", "mirtazapina", "trazodona"];
const ALPHA2 = ["clonidina", "atensina", "catapres", "guanfacina"];
const ATOMOXETINE = ["atomoxetina", "atentah", "strattera"];
const VALPROATE = [
  "divalproato",
  "valproato",
  "ácido valproico",
  "acido valproico",
  "depakote",
  "torval",
  "valpakine",
];

function canonicalDoseKey(name: string) {
  return canonicalSubstanceKey(name);
}

function expectedWindowHours(key: string) {
  if (key === "lisdexanfetamina")
    return { active: 14, tail: 24, label: "pró-fármaco anfetamínico" };
  if (key === "metilfenidato")
    return { active: 4, tail: 8, label: "metilfenidato IR/LA/OROS variável" };
  if (key === "atomoxetina")
    return { active: 24, tail: 72, label: "NRI de uso contínuo" };
  if (key === "clonazepam")
    return {
      active: 12,
      tail: 72,
      label: "benzodiazepínico de meia-vida longa",
    };
  if (key === "valproato")
    return { active: 24, tail: 72, label: "estabilizador/ER" };
  return { active: 8, tail: 24, label: "janela conservadora" };
}

export function runInteractionEngine(
  input: InteractionInput,
): InteractionFinding[] {
  const findings: InteractionFinding[] = [];
  const allNames = [
    ...input.activeMedications.map((m) => m.name),
    ...input.recentDoses.map((d) => d.substanceName),
    ...input.recentSubstanceUse.map((s) => s.substanceName),
  ];
  const allCanonicalKeys = uniqueCanonicalKeys(allNames);
  const allCanonicalLabels = allCanonicalKeys.map((key) =>
    canonicalDisplayLabel(key),
  );
  const has = (list: string[]) => allNames.find((n) => includesAny(n, list));

  const stim = has(STIMULANTS);
  const benzo = has(BENZOS);
  const opi = has(OPIOIDS);
  const sed = has(SEDATIVE);
  const alpha2 = has(ALPHA2);
  const atomoxetine = has(ATOMOXETINE);
  const valproate = has(VALPROATE);
  const caffeine = allNames.find(
    (n) => norm(n).includes("cafeína") || norm(n).includes("cafeina"),
  );
  const doseCaffeine = input.recentDoses.find(
    (d) => Number(d.caffeineMg) >= 150,
  );
  const alcool = allNames.find(
    (n) => norm(n).includes("álcool") || norm(n).includes("alcool"),
  );
  const bupropiona = allNames.find((n) => norm(n).includes("bupropiona"));
  const vortioxetina = allNames.find((n) => norm(n).includes("vortioxetina"));

  if (stim && (caffeine || doseCaffeine)) {
    findings.push({
      category: "farmacodinamica",
      relevance: "monitorar",
      summary: "Estimulante + cafeína: possível ruído autonômico e insônia.",
      involvedSubstances: [
        stim,
        caffeine ?? `cafeína ${doseCaffeine?.caffeineMg ?? "registrada"} mg`,
      ],
      mechanism: "Soma de drive noradrenérgico/adenosinérgico",
      monitor: ["taquicardia", "ansiedade", "insônia"],
      action: "Considerar reduzir cafeína ou afastá-la do horário de pico.",
      confidence: "moderada",
    });
  }

  const sleepRiskDose = input.recentDoses.find(
    (d) => Number(d.sleepDeprivation) >= 6,
  );
  if (stim && sleepRiskDose) {
    findings.push({
      category: "contextual",
      relevance: "relevante",
      summary:
        "Estimulante + privação de sono: maior risco de irritabilidade, redose e fenômeno psicotomimético.",
      detail:
        "A curva teórica pode estar correta, mas a fenomenologia muda: mais ruído autonômico, menor tolerância à frustração, maior chance de paranoia/ideias de referência e falsa leitura de 'não funcionou'.",
      involvedSubstances: [stim, sleepRiskDose.substanceName],
      mechanism:
        "Privação reduz controle pré-frontal e aumenta saliência/reatividade catecolaminérgica.",
      monitor: [
        "sono",
        "cafeína",
        "PA/FC",
        "irritabilidade",
        "paranoia",
        "motivação da redose",
      ],
      action:
        "Não interpretar resposta ruim como falha primária do fármaco antes de corrigir sono/cafeína.",
      confidence: "alta",
    });
  }

  if (stim && atomoxetine) {
    findings.push({
      category: "farmacodinamica",
      relevance: "cautela",
      summary:
        "Estimulante + atomoxetina: somação noradrenérgica/catecolaminérgica.",
      detail:
        "Pode ser racional para cobertura executiva, mas aumenta risco de PA/FC, insônia, irritabilidade, sudorese, ansiedade autonômica e sintomas psicotomiméticos em vulneráveis.",
      involvedSubstances: [stim, atomoxetine],
      mechanism:
        "LDX aumenta DA/NE por liberação; atomoxetina aumenta NE e DA pré-frontal via NET.",
      monitor: [
        "PA",
        "FC",
        "sono",
        "irritabilidade",
        "ansiedade",
        "paranoia",
        "redose",
      ],
      action:
        "Usar dose baixa/monitorada e revisar cafeína/sono antes de atribuir efeito adverso a uma só substância.",
      confidence: "alta",
    });
  }

  if (stim && sed) {
    findings.push({
      category: "farmacodinamica",
      relevance: "cautela",
      summary:
        "Estimulante + sedativo: tensão pró-vigília x anti-hiperarousal.",
      involvedSubstances: [stim, sed],
      detail:
        "Pode ser racional no eixo sono/impulsividade, mas avaliar timing.",
      monitor: ["sedação residual matinal", "hiperfoco estéril", "insônia"],
      confidence: "moderada",
    });
  }

  if (benzo && (opi || alcool)) {
    findings.push({
      category: "farmacodinamica",
      relevance: "relevante",
      summary: "Benzodiazepínico + opioide/álcool: risco de depressão do SNC.",
      involvedSubstances: [benzo, opi ?? alcool!].filter(Boolean) as string[],
      mechanism:
        "Potenciação GABAérgica + depressão respiratória opioide/álcool",
      monitor: ["sedação", "frequência respiratória", "nível de consciência"],
      action: "Discutir desprescrição/redução; orientar redução de dano.",
      confidence: "alta",
    });
  }

  if (bupropiona && vortioxetina) {
    findings.push({
      category: "metabolica",
      relevance: "cautela",
      summary:
        "Bupropiona inibe CYP2D6 — pode elevar exposição à vortioxetina.",
      involvedSubstances: [bupropiona, vortioxetina],
      mechanism: "Inibição CYP2D6",
      monitor: ["náusea", "tontura", "efeitos adversos da vortioxetina"],
      action: "Considerar dose menor da vortioxetina.",
      confidence: "alta",
    });
  }

  if (alpha2 && sed) {
    findings.push({
      category: "farmacodinamica",
      relevance: "monitorar",
      summary: "Alfa-2 agonista + sedativo: hipotensão e sedação contextuais.",
      involvedSubstances: [alpha2, sed],
      monitor: ["PA", "tontura ortostática", "sedação diurna"],
      confidence: "moderada",
    });
  }

  if (alpha2 && benzo) {
    findings.push({
      category: "farmacodinamica",
      relevance: "cautela",
      summary:
        "Clonidina/agonista alfa-2 + benzodiazepínico: sedação, hipotensão e ressaca cognitiva.",
      involvedSubstances: [alpha2, benzo],
      mechanism: "Redução noradrenérgica central + facilitação GABA-A.",
      monitor: [
        "PA",
        "FC",
        "tontura",
        "sedação matinal",
        "cognição",
        "compensação com estimulante",
      ],
      action:
        "Separar efeito terapêutico de sono de ressaca/embotamento executivo no dia seguinte.",
      confidence: "alta",
    });
  }

  if (valproate && benzo) {
    findings.push({
      category: "farmacodinamica",
      relevance: "monitorar",
      summary:
        "Valproato/divalproato + benzodiazepínico: carga sedativa/cognitiva somada.",
      involvedSubstances: [valproate, benzo],
      mechanism: "Modulação GABA/glutamato + facilitação GABA-A.",
      monitor: [
        "sedação",
        "tremor",
        "lentificação",
        "memória",
        "função executiva",
      ],
      action: "Diferenciar estabilização de humor de embotamento funcional.",
      confidence: "moderada",
    });
  }

  const redoseGroups = new Map<string, typeof input.recentDoses>();
  for (const d of input.recentDoses.filter((dose) =>
    isExplicitRedoseLike(dose.logType),
  )) {
    const key = canonicalDoseKey(d.substanceName || "");
    if (!key) continue;
    if (!redoseGroups.has(key)) redoseGroups.set(key, [] as any);
    redoseGroups.get(key)!.push(d);
  }
  for (const [key, group] of redoseGroups.entries()) {
    findings.push({
      category: "comportamental",
      relevance: isStimulantKey(key) ? "relevante" : "cautela",
      summary: `${canonicalDisplayLabel(key)}: ${group.length} registro(s) explícito(s) de redose/uso problemático no período.`,
      detail:
        "Redose/uso problemático é evento clínico em si. O sistema deve diferenciar reforço negativo por travamento/anergia, busca de euforia, tolerância subjetiva, privação de sono e falha real de cobertura.",
      involvedSubstances: Array.from(
        new Set(group.map((g) => g.substanceName)),
      ),
      mechanism:
        "Comportamento de repetição de dose + curva PK/PD relativa + contexto; não interpretar automaticamente como dose insuficiente.",
      monitor: [
        "gatilho",
        "fase da curva",
        "sono",
        "cafeína",
        "PA/FC",
        "ansiedade/paranoia",
        "craving",
        "benefício real vs reforço",
      ],
      action:
        "Registrar check-in 1–3h após a redose e comparar com dose única em dia com sono/cafeína controlados.",
      confidence: "alta",
    });
  }

  // Interação intrassubstância agregada: não repetir o mesmo alerta várias vezes.
  const doseGroups = new Map<string, typeof input.recentDoses>();
  for (const d of input.recentDoses) {
    const key = canonicalDoseKey(d.substanceName || "");
    if (!key) continue;
    if (!doseGroups.has(key)) doseGroups.set(key, [] as any);
    doseGroups.get(key)!.push(d);
  }
  for (const [key, group] of doseGroups.entries()) {
    if (group.length < 2) continue;
    const sorted = [...group].sort(
      (a, b) =>
        new Date(a.actualTime).getTime() - new Date(b.actualTime).getTime(),
    );
    const explicitInGroup = sorted.some((d) => isExplicitRedoseLike(d.logType));
    const isMaintenanceOnly =
      isLongHalfLifeMaintenanceKey(key) &&
      sorted.every((d) => isMaintenanceLikeLog(d.logType)) &&
      !explicitInGroup;
    if (isMaintenanceOnly) continue;

    const win = expectedWindowHours(key);
    const activeGaps: number[] = [];
    const tailGaps: number[] = [];
    for (let i = 1; i < sorted.length; i++) {
      const gap =
        (new Date(sorted[i].actualTime).getTime() -
          new Date(sorted[i - 1].actualTime).getTime()) /
        3_600_000;
      if (!Number.isFinite(gap) || gap < 0) continue;
      const shouldFlag = shouldFlagSelfOverlap({
        name: key,
        logType: sorted[i].logType,
        gapHours: gap,
        explicitRedoseInGroup: explicitInGroup,
      });
      if (!shouldFlag) continue;
      if (gap <= win.active) activeGaps.push(gap);
      else if (gap <= win.tail) tailGaps.push(gap);
    }

    if (activeGaps.length) {
      const minGap = Math.min(...activeGaps);
      findings.push({
        category: "comportamental",
        relevance: isStimulantKey(key) ? "relevante" : "cautela",
        summary: `${canonicalDisplayLabel(key)}: ${activeGaps.length} sobreposição(ões) intrassubstância; menor intervalo ${minGap.toFixed(1)} h.`,
        detail: `Agregado para evitar poluição visual. A segunda dose ocorreu dentro da janela funcional estimada (${win.active} h). Para ${win.label}, tende a prolongar área funcional/cauda e aumentar adversos, não somar eficácia linearmente.`,
        involvedSubstances: Array.from(
          new Set(sorted.map((d) => d.substanceName)),
        ),
        mechanism:
          "Sobreposição temporal de curvas PK/PD relativas; possível saturação de benefício e amplificação de eventos adversos.",
        monitor: isStimulantKey(key)
          ? [
              "sono",
              "PA/FC",
              "irritabilidade",
              "paranoia",
              "cafeína",
              "motivação da redose",
              "benefício real vs reforço",
            ]
          : ["sedação/adversos", "sono", "benefício real", "queda cognitiva"],
        action: isStimulantKey(key)
          ? "Criar regra de dose única, barreira comportamental e check-in obrigatório antes de qualquer redose."
          : "Checar se foi manutenção, SOS ou erro de registro antes de classificar como risco real.",
        confidence: "alta",
      });
    } else if (tailGaps.length && isPotentiallyAcuteOrPrnKey(key)) {
      const minGap = Math.min(...tailGaps);
      findings.push({
        category: "comportamental",
        relevance: "monitorar",
        summary: `${canonicalDisplayLabel(key)}: ${tailGaps.length} nova(s) dose(s) em cauda residual; menor intervalo ${minGap.toFixed(1)} h.`,
        detail:
          "Fora do pico principal, a cauda residual ainda pode contaminar sono, sedação, ansiedade, resposta percebida ou compensação no dia seguinte.",
        involvedSubstances: Array.from(
          new Set(sorted.map((d) => d.substanceName)),
        ),
        mechanism: "Carry-over farmacodinâmico/contextual.",
        monitor: [
          "sono",
          "ressaca/residual",
          "ansiedade",
          "efeito percebido",
          "próxima manhã",
        ],
        confidence: "moderada",
      });
    }
  }

  // Cargas farmacológicas globais — mais útil clinicamente que lista par-a-par isolada.
  const serotonergic = allCanonicalLabels.filter((n) =>
    includesAny(n, [
      "vortioxetina",
      "vurtuoso",
      "vilazodona",
      "sertralina",
      "escitalopram",
      "fluoxetina",
      "fluvoxamina",
      "paroxetina",
      "venlafaxina",
      "desvenlafaxina",
      "duloxetina",
      "clomipramina",
      "tramadol",
      "mdma",
      "ecstasy",
      "linezolida",
      "metadona",
    ]),
  );
  if (serotonergic.length >= 2) {
    findings.push({
      category: "farmacodinamica",
      relevance: serotonergic.some((x) =>
        includesAny(x, ["mdma", "tramadol", "linezolida"]),
      )
        ? "relevante"
        : "cautela",
      summary: `Carga serotoninérgica combinada: ${serotonergic.slice(0, 4).join(" + ")}.`,
      detail:
        "Não é contraindicação automática entre antidepressivo + estimulante, mas o risco passa a depender de dose, vulnerabilidade, outras substâncias e sintomas autonômicos/neuromusculares.",
      involvedSubstances: serotonergic,
      mechanism:
        "Somação serotoninérgica por SERT/modulação 5-HT/liberação serotoninérgica ou opioides serotoninérgicos.",
      monitor: [
        "hiperreflexia",
        "tremor",
        "sudorese",
        "diarreia",
        "confusão",
        "febre",
        "mioclonia",
        "taquicardia",
      ],
      action:
        "Registrar sintomas neuromusculares/autonômicos se houver múltiplos serotoninérgicos; diferenciar ansiedade de síndrome serotoninérgica real.",
      confidence: "moderada",
    });
  }

  const sedativeLoad = allCanonicalLabels.filter((n) =>
    includesAny(n, [
      ...BENZOS,
      ...SEDATIVE,
      "zolpidem",
      "pregabalina",
      "gabapentina",
      "prometazina",
      "hidroxizina",
      "alcool",
      "álcool",
      "valproato",
      "divalproato",
      "clonidina",
    ]),
  );
  if (sedativeLoad.length >= 3) {
    findings.push({
      category: "farmacodinamica",
      relevance: sedativeLoad.some((x) => includesAny(x, OPIOIDS))
        ? "urgente"
        : "cautela",
      summary: `Carga sedativa/cognitiva acumulada: ${sedativeLoad.slice(0, 5).join(" + ")}.`,
      detail:
        "O problema pode aparecer como apatia, baixa iniciativa, ressaca, lentificação, queda executiva e compensação com estimulante — não apenas 'sono'.",
      involvedSubstances: sedativeLoad,
      mechanism:
        "Somação GABAérgica, anti-histamínica, antiadrenérgica e/ou estabilizadora com prejuízo psicomotor e cognitivo.",
      monitor: [
        "sedação matinal",
        "memória",
        "coordenação",
        "queda de drive",
        "redose de estimulante",
        "PA/FC",
      ],
      action:
        "Mapear horário de sedação e comparar com necessidade de redose/estimulante no dia seguinte.",
      confidence: "alta",
    });
  }

  const cyp2d6Inhibitors = allCanonicalLabels.filter((n) =>
    includesAny(n, [
      "bupropiona",
      "wellbutrin",
      "zetron",
      "fluoxetina",
      "paroxetina",
      "quinidina",
    ]),
  );
  const cyp2d6Substrates = allCanonicalLabels.filter((n) =>
    includesAny(n, [
      "atomoxetina",
      "atentah",
      "vortioxetina",
      "vurtuoso",
      "aripiprazol",
      "risperidona",
      "venlafaxina",
    ]),
  );
  if (cyp2d6Inhibitors.length && cyp2d6Substrates.length) {
    findings.push({
      category: "metabolica",
      relevance: "cautela",
      summary: `Possível interação CYP2D6: ${cyp2d6Inhibitors[0]} pode elevar exposição de ${cyp2d6Substrates.slice(0, 3).join(", ")}.`,
      detail:
        "A consequência clínica pode ser náusea/ativação com vortioxetina, PA/FC/irritabilidade com atomoxetina, ou acatisia com antipsicóticos parciais.",
      involvedSubstances: [...cyp2d6Inhibitors, ...cyp2d6Substrates],
      mechanism:
        "Inibição CYP2D6 com aumento de AUC/Cmax de substratos sensíveis.",
      monitor: [
        "náusea",
        "insônia",
        "irritabilidade",
        "PA/FC",
        "acatisia",
        "sonolência",
      ],
      action:
        "Checar dose, titulação e tempo de início; evitar atribuir tudo ao diagnóstico antes de revisar CYP2D6.",
      confidence: "alta",
    });
  }

  const lamotrigina = allCanonicalLabels.find((n) =>
    includesAny(n, ["lamotrigina", "lamictal"]),
  );
  if (lamotrigina && valproate) {
    findings.push({
      category: "metabolica",
      relevance: "urgente",
      summary:
        "Valproato + lamotrigina: interação crítica por aumento de lamotrigina e risco de rash grave/SJS.",
      involvedSubstances: [valproate, lamotrigina],
      mechanism: "Valproato inibe glucuronidação/clearance da lamotrigina.",
      monitor: [
        "rash",
        "febre",
        "mucosa",
        "linfonodos",
        "eosinofilia",
        "titulação",
      ],
      action:
        "Titulação de lamotrigina deve ser muito mais lenta; qualquer rash relevante exige reavaliação imediata.",
      confidence: "alta",
    });
  }

  // Matriz clínica estruturada por canônico: aplica regras editáveis com rastreabilidade
  // de mecanismo/evidência e, principalmente, relevância temporal baseada na última exposição.
  const matrixRules: ClinicalInteractionMatrixRule[] = [];
  const seenMatrix = new Set<string>();
  for (const template of getRuntimeKnowledgeBase()) {
    for (const rule of getInteractionMatrixRules(template.interaction_rules)) {
      const dedupe = `${rule.id}::${canonicalDoseKey(rule.match_a)}::${canonicalDoseKey(rule.match_b)}`;
      if (seenMatrix.has(dedupe)) continue;
      seenMatrix.add(dedupe);
      matrixRules.push(rule);
    }
  }

  const exposureTime = (name: string) => {
    const actual = input.recentDoses
      .filter((d) => canonicalDoseKey(d.substanceName) === canonicalDoseKey(name))
      .map((d) => new Date(d.actualTime).getTime())
      .filter(Number.isFinite);
    const use = input.recentSubstanceUse
      .filter((s) => canonicalDoseKey(s.substanceName) === canonicalDoseKey(name))
      .map((s) => new Date(s.usedAt).getTime())
      .filter(Number.isFinite);
    const observed = [...actual, ...use];
    if (observed.length) return Math.max(...observed);

    const medication = input.activeMedications.find(
      (m) => canonicalDoseKey(m.name) === canonicalDoseKey(name),
    );
    if (
      medication?.currentDose != null &&
      isLongHalfLifeMaintenanceKey(canonicalDoseKey(name)) &&
      !/prn|sos|se necessário|se necessario|eventual/i.test(medication.frequency ?? "")
    ) {
      return now.getTime();
    }
    return null;
  };

  const matrixNames = Array.from(new Set([
    ...input.activeMedications.map((m) => m.name),
    ...input.recentDoses.map((d) => d.substanceName),
    ...input.recentSubstanceUse.map((s) => s.substanceName),
  ])).filter(Boolean);

  for (const rule of matrixRules) {
    const matchedA = matrixNames.filter((name) => includesAny(name, [rule.match_a]));
    const matchedB = matrixNames.filter((name) => includesAny(name, [rule.match_b]));
    if (!matchedA.length || !matchedB.length) continue;
    const pair = matchedA.flatMap((a) => matchedB.map((b) => [a, b] as const)).find(([a, b]) => canonicalDoseKey(a) !== canonicalDoseKey(b));
    if (!pair) continue;

    const latestA = exposureTime(pair[0]);
    const latestB = exposureTime(pair[1]);
    const latest = [latestA, latestB].filter((x): x is number => x !== null);
    const now = Date.now();
    const hoursSinceLatest = latest.length ? (now - Math.max(...latest)) / 3_600_000 : 0;
    const temporal = temporalizeMatrixRule(rule, Math.max(0, hoursSinceLatest));
    if (!temporal.include) continue;

    const temporalNote = temporal.temporalNote;
    const evidence = rule.evidence_grade && rule.evidence_grade !== "desconhecida"
      ? ` Evidência: ${rule.evidence_grade}.`
      : " Evidência não classificada na base; não presumir baixa relevância.";
    findings.push({
      category: rule.category ?? "farmacodinamica",
      relevance: temporal.severity ?? "monitorar",
      summary: rule.effect
        ? `${pair[0]} + ${pair[1]}: ${rule.effect}`
        : `Interação matriz: ${pair[0]} + ${pair[1]}.`,
      detail: [
        rule.interpretation,
        rule.notes,
        rule.dose_dependence ? `Dependência de dose: ${rule.dose_dependence}.` : "",
        temporalNote,
      ].filter(Boolean).join(" "),
      involvedSubstances: [pair[0], pair[1]],
      mechanism: [rule.mechanism, rule.pk_mechanism, rule.pd_axis ? `Eixo PD: ${rule.pd_axis}.` : ""].filter(Boolean).join(" "),
      monitor: rule.monitor,
      action: rule.action,
      confidence: rule.confidence ?? "moderada",
      evidence: rule.source ? `${rule.source}${rule.source_url ? ` — ${rule.source_url}` : ""}` : undefined,
      phenotype: rule.phenotype,
      redFlags: rule.red_flags,
      temporalBasis: temporalNote,
    });
  }

  if ((input.sessionContext?.sleepHours ?? 99) < 5) {
    findings.push({
      category: "contextual",
      relevance: "monitorar",
      summary: "Sono < 5 h — interpretar respostas medicamentosas com cautela.",
      involvedSubstances: [],
      confidence: "alta",
    });
  }

  // Enriquecimento final orientado ao momento atual.
  // Prescrições ativas precisam participar da fotografia temporal mesmo quando o médico
  // ainda não lançou a tomada de hoje. Não tratamos isso como adesão confirmada:
  // é uma exposição PRESCRITA estimada, útil para carry-over/steady-state.
  const now = new Date();
  const actualKeys = new Set(
    input.recentDoses.map((d) => canonicalDoseKey(d.substanceName)).filter(Boolean),
  );
  const prescribedExposureDoses = input.activeMedications
    .filter((m) => {
      const key = canonicalDoseKey(m.name);
      const freq = norm(m.frequency ?? "");
      const prn = /prn|sos|se necessário|se necessario|eventual/.test(freq);
      return Boolean(key && m.currentDose != null && !actualKeys.has(key) && !prn && isLongHalfLifeMaintenanceKey(key));
    })
    .map((m) => ({
      substanceName: m.name,
      actualTime: now,
      doseAmount: m.currentDose ?? null,
      logType: "prescricao_ativa_estimada",
    }));
  const temporalDoses = input.exposureDoses ?? [...input.recentDoses, ...prescribedExposureDoses] as any[];

  const current = buildSessionContext({
    allDoses: temporalDoses,
    sessionDoses: temporalDoses,
    at: now,
    knowledge: getRuntimeKnowledgeBase(),
    bodyWeightKg: input.bodyWeightKg,
  });
  // Include contextual mechanisms, not merely re-rank the legacy keyword alerts.
  for (const interaction of current.interactions) {
    findings.push({
      category: interaction.id === "cocaina-alcool" ? "metabolica" : "farmacodinamica",
      relevance: interaction.level === "alto" || interaction.level === "emergencia" ? "urgente" : interaction.level,
      summary: interaction.title,
      detail: interaction.rationale,
      involvedSubstances: interaction.involved,
      mechanism: interaction.mechanism,
      monitor: interaction.monitor,
      confidence: interaction.confidence,
      whyNow: interaction.context,
      redFlags: interaction.redFlags,
      evidence: interaction.id === "cocaina-alcool" ? "Revisão sistemática (2024), PMID 38592322 — https://pubmed.ncbi.nlm.nih.gov/38592322/" : undefined,
    });
  }
  const stateByName = new Map(current.states.map((state) => [canonicalDoseKey(state.name), state]));
  const relevanceOrder: InteractionRelevance[] = ["informativa", "monitorar", "cautela", "relevante", "urgente"];
  const enriched = findings.map((finding) => {
    const states = finding.involvedSubstances.map((name) => stateByName.get(canonicalDoseKey(name))).filter(Boolean) as any[];
    if (!states.length) return finding;
    const currentEffects = states.map((s) => s.effect0to100);
    const active = states.filter((s) => ["subida", "pico", "platô", "steady-state"].includes(s.phase) && s.effect0to100 >= 25);
    const recentDose = states.map((s) => s.latestDose).filter(Boolean).map((d: any) => new Date(d.actual_time ?? d.actualTime).getTime()).filter(Number.isFinite).sort((a: number, b: number) => b - a)[0];
    const hours = recentDose != null ? Math.max(0, (Date.now() - recentDose) / 3600000) : null;
    const carryOnly = states.every((s) => s.carryOver && s.effect0to100 < 25);
    let relevance = finding.relevance;
    let temporalBasis = "exposição atual não modelada com detalhe suficiente";
    if (active.length) temporalBasis = `fase ativa: ${active.map((s) => `${s.name}=${s.phase} ${s.effect0to100.toFixed(0)}/100`).join("; ")}`;
    else if (/cocaetileno/i.test(finding.mechanism ?? "")) temporalBasis = "coexposição identificada; exposição ao metabólito não quantificada pelas curvas originais";
    else if (carryOnly) { const idx = relevanceOrder.indexOf(relevance); relevance = relevanceOrder[Math.max(0, idx - 1)]; temporalBasis = "apenas carry-over/cauda com efeito relativo baixo no momento"; }
    else if (currentEffects.every((v) => v < 40)) { const idx = relevanceOrder.indexOf(relevance); relevance = relevanceOrder[Math.max(0, idx - 1)]; temporalBasis = "efeito relativo atual baixo, sem escalada de dose identificada"; }
    else temporalBasis = `efeito relativo atual ${Math.max(...currentEffects).toFixed(0)}/100`;
    const estimatedPrescription = states.some((s) =>
      s.latestDose?.log_type === "prescricao_ativa_estimada"
    );
    const latestText = estimatedPrescription
      ? "exposição de manutenção estimada a partir da prescrição ativa; adesão não confirmada"
      : hours != null
        ? `última exposição há ${hours.toFixed(1)} h`
        : "sem horário recente disponível";
    const phaseText = states.map((s) => `${s.name}: ${s.phase}`).join(" · ");
    return { ...finding, relevance, whyNow: `${latestText}; ${phaseText}; ${temporalBasis}.`, temporalBasis };
  });
  // Consolida alertas que representam o mesmo fenômeno clínico.
  const family = (f: InteractionFinding) => {
    const textValue = `${f.summary} ${f.mechanism ?? ""}`.toLowerCase();
    if (/cocaetileno|cocaethylene/.test(textValue)) return "cocaine-alcohol";
    if (/respirat|depressor.*snc|sedativ.*op/i.test(textValue)) return "cns-respiratory";
    if (/seroton/i.test(textValue)) return "serotonergic";
    if (/simpatic|catecol|estimulante/i.test(textValue)) return "sympathomimetic";
    if (/cyp|metabol/i.test(textValue)) return `metabolic:${f.involvedSubstances.slice().sort().join("|")}`;
    if (/redose|sobreposi|carry-over/i.test(textValue)) return `temporal-overlap:${f.involvedSubstances.slice().sort().join("|")}`;
    return f.category;
  };
  const merged = new Map<string, InteractionFinding>();
  for (const f of enriched) {
    const key = family(f);
    const previous = merged.get(key);
    if (!previous) { merged.set(key, { ...f, involvedSubstances: [...new Set(f.involvedSubstances)], monitor: [...new Set(f.monitor ?? [])], phenotype: [...new Set(f.phenotype ?? [])], redFlags: [...new Set(f.redFlags ?? [])] }); continue; }
    const stronger = relevanceOrder.indexOf(f.relevance) > relevanceOrder.indexOf(previous.relevance) ? f : previous;
    merged.set(key, { ...stronger, involvedSubstances: [...new Set([...previous.involvedSubstances, ...f.involvedSubstances])], monitor: [...new Set([...(previous.monitor ?? []), ...(f.monitor ?? [])])], phenotype: [...new Set([...(previous.phenotype ?? []), ...(f.phenotype ?? [])])], redFlags: [...new Set([...(previous.redFlags ?? []), ...(f.redFlags ?? [])])], detail: [previous.detail, f.detail].filter(Boolean).join(" "), action: [previous.action, f.action].filter(Boolean).join(" "), whyNow: [previous.whyNow, f.whyNow].filter(Boolean).join(" ") });
  }
  return [...merged.values()].sort((a, b) => relevanceOrder.indexOf(b.relevance) - relevanceOrder.indexOf(a.relevance));
}
